import AccessTime from '@mui/icons-material/AccessTime'
import { Button, DialogActions, DialogContent, Box, Avatar } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import {
  CustomSelect,
  selectTypes,
  TimeRangePicker,
} from 'src/consumer/components/helperComponents'
import { genericHandleChange } from 'src/consumer/helpers/utilFunctions'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  getImageLink,
} from 'src/mentor/helpers/utilityFunctions'
import { postSPData } from 'src/supportPortal/services/services'
import { mediaURL } from 'src/consumer/services'

export default function RosterForm({
  selectedSlot,
  selectedItem,
  legendItems,
  getSidebarLegendValue,
  getSidebarLegendLabel,
  updateSlotOnCalendar,
  addEventToSlotList,
  slotSaveApiSignature = '/api/secure/cc/support/roster/assign-therapist-to-chamber',
  dropDownKey = 'mentorId',
  dropDownLabel = 'Mentor',
  closePopup,
  transformToSaveItem = ({ rosterSaveItem, allSidebarItems }) => {
    return rosterSaveItem
  },
  rosterSchedule = null, // Roster schedule for the room/date
}) {
  const [rosterSlot, setRosterSlot] = useState()
  const currentDateInDayJs = dayjs(rosterSlot?.customDate)
  const [saving, setSaving] = useState(false)
  
  // Generate time options based on roster schedule
  const timeOptions = useMemo(() => {
    const valueFormat = 'HH:mm'
    const labelFormat = 'HH:mm'
    const options = []
    
    if (!rosterSchedule || !rosterSchedule.weeklyHourMap) {
      // Fallback: generate all 15-minute intervals for the day
      let time = dayjs().startOf('day')
      while (time.isBefore(dayjs().endOf('day'))) {
        options.push({ value: time.format(valueFormat), label: time.format(labelFormat) })
        time = time.add(15, 'minute')
      }
      return options
    }
    
    // Get day of week (monday, tuesday, etc.)
    const dayOfWeek = currentDateInDayJs.format('dddd').toLowerCase()
    const dayHours = rosterSchedule.weeklyHourMap[dayOfWeek] || []
    
    if (dayHours.length === 0) {
      // No hours for this day
      return []
    }
    
    // Generate time slots for all time ranges on this day
    dayHours.forEach((timeRange) => {
      if (!timeRange.start || !timeRange.end) return
      
      let time = dayjs(timeRange.start, 'HH:mm')
      const endTime = dayjs(timeRange.end, 'HH:mm')
      
      // Generate 15-minute intervals from start to end
      while (time.isBefore(endTime)) {
        options.push({ value: time.format(valueFormat), label: time.format(labelFormat) })
        time = time.add(15, 'minute')
      }
      
      // Always include the end time, even if it's not on a 15-minute boundary
      // This allows slots to end exactly at the roster schedule end time
      // Only add if it wasn't already added (i.e., if end time is not on a 15-minute boundary)
      if (!time.isSame(endTime)) {
        options.push({ value: endTime.format(valueFormat), label: endTime.format(labelFormat) })
      }
    })
    
    // Remove duplicates and sort
    const uniqueOptions = Array.from(
      new Map(options.map((opt) => [opt.value, opt])).values()
    ).sort((a, b) => a.value.localeCompare(b.value))
    
    return uniqueOptions
  }, [rosterSchedule, currentDateInDayJs])
  
  // Get "to" time options - start from the next slot after "from", including end time
  const toTimeOptions = useMemo(() => {
    if (!rosterSlot?.slotStartTime || !timeOptions.length) {
      return timeOptions
    }
    
    const startTime = dayjs(rosterSlot.slotStartTime, 'HH:mm')
    
    // Filter to only show times after the start time (including end time of schedule)
    // This allows selecting the end time of the roster schedule
    return timeOptions.filter((opt) => {
      const optTime = dayjs(opt.value, 'HH:mm')
      return optTime.isAfter(startTime)
    })
  }, [timeOptions, rosterSlot?.slotStartTime])
  
  // Validate custom time input - check if it's within roster schedule
  const validateCustomTime = useCallback((time, field) => {
    if (!time) {
      return false
    }
    
    // Check if time is valid format (HH:mm)
    const timeObj = dayjs(time, 'HH:mm', true)
    if (!timeObj.isValid()) {
      return false
    }
    
    if (!rosterSchedule || !rosterSchedule.weeklyHourMap) {
      // No schedule constraint - allow any valid time
      return true
    }
    
    // Get day of week
    const dayOfWeek = currentDateInDayJs.format('dddd').toLowerCase()
    const dayHours = rosterSchedule.weeklyHourMap[dayOfWeek] || []
    
    if (dayHours.length === 0) {
      return false // No hours for this day
    }
    
    // Check if time falls within any of the time ranges for this day
    const timeInMinutes = timeObj.hour() * 60 + timeObj.minute()
    
    const isValid = dayHours.some((timeRange) => {
      if (!timeRange.start || !timeRange.end) return false
      
      const rangeStart = dayjs(timeRange.start, 'HH:mm')
      const rangeEnd = dayjs(timeRange.end, 'HH:mm')
      const rangeStartMinutes = rangeStart.hour() * 60 + rangeStart.minute()
      const rangeEndMinutes = rangeEnd.hour() * 60 + rangeEnd.minute()
      
      // Time must be within the range
      // For start: >= start and < end (can start at opening, but must end before closing)
      // For end: > start and <= end (must be after start, can end at or before closing)
      if (field === 'start') {
        return timeInMinutes >= rangeStartMinutes && timeInMinutes < rangeEndMinutes
      } else {
        // For end time, allow up to and including the end time
        return timeInMinutes > rangeStartMinutes && timeInMinutes <= rangeEndMinutes
      }
    })
    
    return isValid
  }, [rosterSchedule, currentDateInDayJs])
  const isRosterSlotValid = useMemo(() => {
    if (!rosterSlot) {
      return false
    }
    if (!rosterSlot?.mentorId) {
      return false
    }
    return true
  }, [rosterSlot])

  async function saveSlot() {
    setSaving(true)
    const resp = await postSPData(
      slotSaveApiSignature,
      transformToSaveItem({ rosterSaveItem: rosterSlot, allSidebarItems: legendItems }),
    )
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const rosterItem = getDataMapFromCommonResponse(resp)?.['clinic-roster-slot']
        addEventToSlotList(rosterItem)
        closePopup()
        return
      } else {
        toast.error(`${getMessageMapFromCommonResponse(resp)}`)
      }
    }
    setSaving(false)
  }

  function handleChangeDate([startTime, endTime]) {
    // If start time changed and end time is now invalid, reset end time to next slot
    let validEndTime = endTime
    if (startTime && endTime) {
      const start = dayjs(startTime, 'HH:mm')
      const end = dayjs(endTime, 'HH:mm')
      
      // If end time is before or equal to start time, set it to next slot
      if (!end.isAfter(start)) {
        validEndTime = start.add(15, 'minute').format('HH:mm')
      }
    } else if (startTime && !endTime) {
      // If only start time is set, set end time to next slot
      validEndTime = dayjs(startTime, 'HH:mm').add(15, 'minute').format('HH:mm')
    }
    
    updateSlotOnCalendar({
      date: rosterSlot?.customDate,
      startTime,
      endTime: validEndTime,
    })

    setRosterSlot((prev) => ({
      ...prev,
      slotStartTime: startTime,
      slotEndTime: validEndTime,
    }))
  }

  function handleChangeRosterSlot(e) {
    genericHandleChange(e, setRosterSlot)
  }

  useEffect(() => {
    // Only update if selectedSlot actually changed (by reference or key fields)
    // This prevents resetting user input when they're typing
    if (selectedSlot) {
      const hasChanged = 
        !rosterSlot ||
        selectedSlot.chamberRoomId !== rosterSlot.chamberRoomId ||
        selectedSlot.customDate !== rosterSlot.customDate ||
        (selectedSlot.slotStartTime !== rosterSlot.slotStartTime && !rosterSlot.slotStartTime) // Only reset if current is empty
        
      if (hasChanged) {
        setRosterSlot(selectedSlot)
      }
    } else {
      setRosterSlot(selectedSlot)
    }
  }, [selectedSlot])

  return (
    <>
      <DialogContent sx={{ p: 3, '&.MuiDialogContent-root': { pt: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AccessTime sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
            <Box>
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Date
              </Box>
              <Box sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                {currentDateInDayJs.format('dddd, D MMMM YYYY')}
              </Box>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              Time Range
            </Box>
            <TimeRangePicker
              value={[rosterSlot?.slotStartTime, rosterSlot?.slotEndTime]}
              onChange={handleChangeDate}
              valueFormat="HH:mm"
              labelFormat="HH:mm"
              timeOptions={timeOptions}
              toTimeOptions={toTimeOptions}
              validateCustomTime={validateCustomTime}
            />
          </Box>
          <Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
              {dropDownLabel}
            </Box>
            <CustomSelect
              id={dropDownKey}
              value={rosterSlot?.[dropDownKey]}
              options={legendItems}
              getOptionValue={getSidebarLegendValue}
              getOptionLabel={getSidebarLegendLabel}
              label={dropDownLabel}
              onChangeEvent={handleChangeRosterSlot}
              selectType={selectTypes.string}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              formatOptionLabel={(option, { context }) => {
                const label = getSidebarLegendLabel(option)
                const profilePicId = option?.profilePicId
                const profilePicUrl = profilePicId ? getImageLink(profilePicId) : null
                
                if (context === 'menu') {
                  // In dropdown menu - show with avatar
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={profilePicUrl}
                        sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                      >
                        {!profilePicUrl && label?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>{label}</Box>
                    </Box>
                  )
                }
                // In selected value - show with avatar
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={profilePicUrl}
                      sx={{ width: 20, height: 20, fontSize: '0.7rem' }}
                    >
                      {!profilePicUrl && label?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>{label}</Box>
                  </Box>
                )
              }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  borderRadius: '8px',
                  borderColor: '#e0e0e0',
                  '&:hover': {
                    borderColor: '#1976d2',
                  },
                }),
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={closePopup}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          disabled={!isRosterSlotValid || saving} 
          onClick={saveSlot} 
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
            '&:disabled': {
              opacity: 0.5,
            },
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </>
  )
}
